/**
 * marketingAttribution.js — Sprint C observability helper.
 *
 * Captures UTM / referrer / landing-page context ONCE per session and
 * stashes it in sessionStorage so it survives in-app navigation but does
 * NOT persist across browser sessions. Read by:
 *   - WellnessAssessment.jsx     (legacy submit)
 *   - Assessment.jsx             (adaptive /start)
 *   - FunnelPage.jsx             (confirm flow)
 *
 * IMPORTANT: this data is observability-only. It is NEVER read by any
 * scoring / routing logic on either frontend or backend. Backend tests
 * assert score-byte-identity with and without these fields.
 */
const STORAGE_KEY = "kcis_marketing_attribution";

const UTM_PARAMS = ["utm_source", "utm_campaign", "utm_medium", "utm_content", "utm_term"];

function _readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _writeStored(obj) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* no-op */
  }
}

/**
 * Capture attribution from the current URL and document.referrer. Idempotent
 * within a session — the first non-empty capture wins so that downstream
 * routes (e.g., /assessment → /funnels/xxx) see the original landing context.
 */
export function captureMarketingAttribution() {
  const existing = _readStored();
  if (existing) return existing;

  let attribution = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of UTM_PARAMS) {
      const v = (params.get(k) || "").trim();
      if (v) attribution[k] = v;
    }
    const referral = (document.referrer || "").trim();
    if (referral) attribution.referral_source = referral;
    attribution.landing_page =
      window.location.pathname + (window.location.search || "");
  } catch {
    /* no-op */
  }

  // Only persist when at least one UTM or a non-empty referrer was found.
  const meaningful = UTM_PARAMS.some((k) => attribution[k]) || attribution.referral_source;
  if (!meaningful) return null;

  _writeStored(attribution);
  return attribution;
}

export function readMarketingAttribution() {
  return _readStored();
}

/** Returns a query string fragment to append to outbound CTA URLs. */
export function attributionQueryString() {
  const attr = _readStored();
  if (!attr) return "";
  const usp = new URLSearchParams();
  for (const k of UTM_PARAMS) {
    if (attr[k]) usp.set(k, attr[k]);
  }
  const str = usp.toString();
  return str ? `&${str}` : "";
}
